// src/hooks/useUserClinicMapping.js
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useProfilePrefetch } from "../context/ProfilePrefetchContext";
import { useRegionalBackend } from "../context/RegionalBackendContext";
import { readGlobalEntryHandoff } from "../utils/navigation/globalEntryHandoff";

function clean(value) {
  if (value === undefined || value === null) return null;
  const next = String(value).trim();
  return next.length ? next : null;
}

function readSubjectIdFromProfile(profile) {
  const normalized = profile ?? null;
  return clean(normalized?.id ?? normalized?.subjectId);
}

export function useUserClinicMapping({ uid }) {
  const { getCachedProfile } = useProfilePrefetch();
  const regionalBackend = useRegionalBackend();
  const location = useLocation();

  return useMemo(() => {
    if (!uid) {
      return {
        loading: false,
        resolved: false,
        subjectId: null,
        clinicStudyId: null,
        inviteToken: null,
        error: null,
      };
    }

    const cachedProfileEntry = getCachedProfile(uid);
    const cachedProfile =
      cachedProfileEntry?.status === "ready" ? cachedProfileEntry.data ?? null : null;
    const resolvedProfile = cachedProfile || regionalBackend.profile || null;
    const handoff = readGlobalEntryHandoff(location.search);
    const subjectId = readSubjectIdFromProfile(resolvedProfile);
    const clinicStudyId =
      clean(resolvedProfile?.clinicStudyId) || handoff.clinicStudyId;
    const inviteToken = handoff.inviteToken;

    if (regionalBackend.loading && !resolvedProfile) {
      return {
        loading: true,
        resolved: false,
        subjectId: null,
        clinicStudyId: null,
        inviteToken,
        error: null,
      };
    }

    return {
      loading: false,
      resolved: true,
      subjectId,
      clinicStudyId,
      inviteToken,
      error: regionalBackend.error ? "fetch_failed" : null,
    };
  }, [
    getCachedProfile,
    location.search,
    regionalBackend.error,
    regionalBackend.loading,
    regionalBackend.profile,
    uid,
  ]);
}
